// Concurrent players and kills over the last few hours.
//
// Inline SVG rather than Chart.js: this sits inside a card and has to stay
// legible at a couple of hundred pixels tall, where a full chart library's
// axes, legend and tooltips are all noise. It also keeps the card cheap to
// render when several are open at once.
//
// Concurrency is reconstructed server-side from session windows, so a long
// session reads as a plateau rather than a spike at spawn.

export type ActivityPoint = { t: string; players: number; kills: number }

export type Activity = {
    points: ActivityPoint[]
    bucket_minutes: number
    hours: number
    peak_players: number
    peak_kills: number
}

const W = 600
const H = 90
const PAD = 4

export const ActivityChart = ({ activity }: { activity: Activity }) => {
    const points = activity.points ?? []
    if (points.length < 2) {
        return <div className='text-xs opacity-30 text-center py-4'>not enough history yet</div>
    }

    // Both series share the x axis but not the y: kills and concurrent players
    // are different units, and forcing one scale would flatten whichever is
    // smaller into the baseline.
    const maxPlayers = Math.max(activity.peak_players, 1)
    const maxKills = Math.max(activity.peak_kills, 1)
    const step = (W - PAD * 2) / (points.length - 1)

    const x = (i: number) => PAD + i * step
    const y = (v: number, max: number) => H - PAD - (v / max) * (H - PAD * 2)

    const line = (key: 'players' | 'kills', max: number) =>
        points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p[key], max).toFixed(1)}`).join(' ')

    const area = `${line('players', maxPlayers)} L ${x(points.length - 1).toFixed(1)} ${H - PAD} L ${x(0).toFixed(1)} ${H - PAD} Z`

    const label = (iso: string) =>
        new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

    return (
        <div className='w-full'>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio='none' className='w-full h-24'>
                <defs>
                    <linearGradient id='playersFill' x1='0' y1='0' x2='0' y2='1'>
                        <stop offset='0%' stopColor='#C91FFF' stopOpacity='0.35' />
                        <stop offset='100%' stopColor='#C91FFF' stopOpacity='0' />
                    </linearGradient>
                </defs>

                {[0.25, 0.5, 0.75].map((f) => (
                    <line
                        key={f}
                        x1={PAD}
                        x2={W - PAD}
                        y1={PAD + f * (H - PAD * 2)}
                        y2={PAD + f * (H - PAD * 2)}
                        stroke='currentColor'
                        strokeOpacity='0.07'
                        strokeWidth='1'
                    />
                ))}

                <path d={area} fill='url(#playersFill)' />
                <path
                    d={line('players', maxPlayers)}
                    fill='none'
                    stroke='#C91FFF'
                    strokeWidth='1.5'
                    vectorEffect='non-scaling-stroke'
                />
                <path
                    d={line('kills', maxKills)}
                    fill='none'
                    stroke='#f59e0b'
                    strokeWidth='1'
                    strokeDasharray='3 3'
                    vectorEffect='non-scaling-stroke'
                />

                {/* One invisible strip per bucket, so hovering anywhere in a
                    column gives that bucket's numbers without a tooltip library. */}
                {points.map((p, i) => (
                    <rect
                        key={p.t}
                        x={x(i) - step / 2}
                        y={0}
                        width={step}
                        height={H}
                        fill='transparent'
                    >
                        <title>{`${label(p.t)}: ${p.players} player(s), ${p.kills} kill(s)`}</title>
                    </rect>
                ))}
            </svg>

            <div className='flex flex-row justify-between text-[10px] opacity-40 mt-1'>
                <span>{label(points[0].t)}</span>
                <span className='flex flex-row gap-3'>
                    <span style={{ color: '#C91FFF' }}>players · peak {activity.peak_players}</span>
                    <span style={{ color: '#f59e0b' }}>kills · peak {activity.peak_kills}</span>
                </span>
                <span>now</span>
            </div>
        </div>
    )
}
